import { Component } from 'react';
import { DiscordSDK } from '@discord/embedded-app-sdk';
import { API_URL } from '../Settings';
import { DiscordContext } from './DiscordContext';
import { isLikelyEmbedded } from './embeddedDetect';

const LOG_TAG = '[DiscordSDK]';
const SDK_SCOPES = ['identify', 'rpc.activities.write', 'guilds.members.read'];

// The OAuth flow needs the Discord App ID to call authorize(). It's
// safe to expose in the client (it's a public identifier). The client
// secret stays server-side and is only used by the backend
// /api/session_auth/discord/ endpoint.
const DISCORD_APP_ID = process.env.REACT_APP_DISCORD_APP_ID || '';

const CSRF_COOKIE_NAME = 'csrftoken';
const CSRF_HEADER_NAME = 'X-CSRFToken';
const CSRF_URL = API_URL + 'session_auth/csrf/';
const DISCORD_AUTH_URL = API_URL + 'session_auth/discord/';
const LOBBY_FROM_PLATFORM_URL = API_URL + 'lobby/from_platform/';

// Mirrors platforms.Platform.DISCORD_ID / BROWSER_ID in the backend
// fixtures. BROWSER is the open-web rail — same `from_platform` endpoint,
// `binding_key = str(user.id)` per `create_browser_lobby` in
// `lobbies/services.py`.
const PLATFORM_DISCORD_ID = 1;
const PLATFORM_BROWSER_ID = 7;
// Window event name is legacy (predates the open-web rendezvous helper
// below). FE listeners — Lobby.js, GamePage.js — key off this name
// regardless of platform; rename is a separate sweep.
const DISCORD_LOBBY_JOINED_EVENT = 'discord-lobby-joined';

// Mirrors lobbies.models.LobbyStatus ids. Mapped JS-side per the CC
// contract — we do NOT import status names from the backend. Used to
// derive each channel-lobby entry's displayState for the Main Menu.
const LOBBY_STATUS_FORMING_ID = 1;
const LOBBY_STATUS_LAUNCHING_ID = 2;
const LOBBY_STATUS_PLAYING_ID = 3;

const CHANNEL_LOBBY_STATE_FORMING = 'forming';
const CHANNEL_LOBBY_STATE_IN_PROGRESS = 'in_progress';

// State.error values surfaced through DiscordContext. Anything not in
// this list is an SDK-init failure carrying its raw message.
const ERROR_AUTHORIZE_FAILED = 'authorize-failed';
const ERROR_AUTHORIZE_NO_CODE = 'authorize-no-code';
const ERROR_BACKEND_TRANSPORT = 'backend-transport';
const ERROR_NO_ACCESS_TOKEN = 'no-access-token';
const ERROR_SDK_AUTHENTICATE_FAILED = 'sdk-authenticate-failed';

// Cross-module signal for API.validateCredentials so it can wait for
// the Discord handshake before deciding whether to fall through to
// anon-register. Window-scoped on purpose: it survives module
// boundaries and React rerenders, and lets late-mounting consumers
// see the resolved state without subscribing to the event stream.
//
//   'pending'  — handshake in flight (or not started yet)
//   'complete' — HSH session cookie is set; whoami will succeed
//   'failed'   — handshake will not produce a session; fall through
//
// The 'complete' bucket includes the no-access-token and
// sdk-authenticate-failed paths because both leave the HSH session
// valid even though the SDK leg failed.
const DISCORD_AUTH_STATE_FLAG = '__discordAuthState__';
const DISCORD_AUTH_COMPLETE_EVENT = 'discord-auth-complete';
const DISCORD_AUTH_FAILED_EVENT = 'discord-auth-failed';

// Cold-start trace stage names, in run order. DiscordBootPanel reads
// the matching keys off context.bootStages. See DiscordContext.js for
// the per-stage shape and the status state machine.
const BOOT_STAGE_NAMES = [
    'embeddedDetect',
    'sdkReady',
    'authorize',
    'csrfPrime',
    'hshSession',
    'sdkAuthenticate',
    'lobbyFetch',
];

const BOOT_STAGE_PENDING = 'pending';
const BOOT_STAGE_OK = 'ok';
const BOOT_STAGE_FAILED = 'failed';
const ERROR_MISSING_APP_ID = 'missing-app-id';
const ERROR_INIT_FAILED = 'init-failed';
const ERROR_FROM_PLATFORM_NULL = 'from-platform-null';

// Third from_platform response shape: the backend routed this peer
// back into an in-progress game (they already hold a seat in the
// channel) instead of seating them in a lobby. Distinguished by this
// action token; the frontend dispatches a WS Resume instead of
// populating the lobby and letting the navigator route.
const ACTION_REJOIN = 'rejoin';

// Deferred-resume backstop: a rejoin can arrive before App has built
// its `game` singleton (auth bootstrap still in flight). We retry on a
// short, BOUNDED series of one-shot timeouts — not a polling interval —
// so a permanently-absent app.game can never spin forever.
const REJOIN_FLUSH_INTERVAL_MS = 25;
const REJOIN_FLUSH_MAX_ATTEMPTS = 80;


/**
 * Build the initial bootStages map — every leg pending with null
 * timestamps. Matches the open-web context default in
 * DiscordContext.js; the provider mutates it in place via _setStage as
 * the embedded handshake progresses.
 */
function makeInitialBootStages() {
    const stages = {};
    for (const name of BOOT_STAGE_NAMES) {
        stages[name] = {
            status: BOOT_STAGE_PENDING,
            startedAt: null,
            completedAt: null,
            error: null,
        };
    }
    return stages;
}


function markDiscordAuthComplete(userIdentity) {
    if (typeof window === 'undefined') {
        return;
    }
    window[DISCORD_AUTH_STATE_FLAG] = 'complete';
    window.dispatchEvent(new CustomEvent(DISCORD_AUTH_COMPLETE_EVENT, {
        detail: { userIdentity },
    }));
}


/**
 * POST a pre-built body to /api/lobby/from_platform/. Returns the JSON
 * payload on success (HTTP 200), or null on any non-2xx / network error
 * — the HSH session is already established at this point, so a failure
 * here is recoverable (the lobby is created on first use anyway).
 *
 * The body is platform-agnostic: callers build the platform_id /
 * binding_key / binding_metadata / lobby_uuid / create_new mix they
 * want. Discord callers go through `joinChannelLobby` / `createChannelLobby`
 * (build a `binding_key = channelId` body with Discord metadata);
 * open-web callers go through `joinBrowserLobby` (build a
 * `binding_key = str(user.id)` body, no metadata). Backend dispatch is
 * the same.
 */
async function postFromPlatform(body) {
    console.info('%s from_platform request body=%s',
        LOG_TAG, JSON.stringify(body));
    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };
    if (csrfToken) {
        headers[CSRF_HEADER_NAME] = csrfToken;
    }
    try {
        const response = await fetch(LOBBY_FROM_PLATFORM_URL, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            // CC_POLITE_JOIN_BOUNCES (2026-06-07): the backend already
            // speaks typed errors (LobbyJoinError.code — lobby_full,
            // wrong_status, not_found, lobby_not_bound_to_channel). This
            // helper used to discard the body and return a bare null, so
            // the join path could only ever show a generic bounce. Read
            // the typed code and hand it up as `{ error, status }` so
            // GamePage.onJoinResult can render a polite, per-error notice.
            // A null code (no/garbled JSON body) falls through to the
            // generic notice copy.
            let errorCode = null;
            try {
                const errorBody = await response.json();
                errorCode = (errorBody && errorBody.error) || null;
            } catch (parseError) {
                errorCode = null;
            }
            console.error('%s from_platform response not ok status=%s'
                + ' error=%s', LOG_TAG, response.status, errorCode);
            return { error: errorCode, status: response.status };
        }
        const payload = await response.json();
        const seats = (payload && payload.lobby && payload.lobby.seats)
            || [];
        const seatUsers = seats.map(
            (s) => (s && s.user && s.user.id) || null);
        console.info('%s from_platform response lobby_uuid=%s lobby_id=%s'
            + ' is_owner=%s seat_index=%s seat_count=%s seat_users=%s',
            LOG_TAG,
            (payload && payload.lobby && payload.lobby.uuid) || null,
            payload && payload.lobby_id,
            payload && payload.is_owner,
            payload && payload.seat_index,
            seats.length,
            JSON.stringify(seatUsers));
        return payload;
    } catch (error) {
        console.error('%s from_platform transport error: %s',
            LOG_TAG, error && error.message);
        // CC_POLITE_JOIN_BOUNCES: a transport failure is still a join
        // failure the caller must surface politely. Return the error
        // shape (null code → generic notice) rather than a bare null so
        // the join path branches the same way it does for a typed 4xx.
        return { error: null, status: 0 };
    }
}


/**
 * GET the channel-bound lobbies list from /api/lobby/from_platform/.
 * Read-only mirror of postFromPlatform: platform_id / binding_key ride
 * the query string, there is no body, and nothing is seated. Returns
 * the parsed JSON (`{ lobbies: [...] }`) or null on any non-2xx /
 * network error — an empty or absent list is a normal "no games live
 * here yet" state, handled by the caller.
 */
async function getFromPlatform(channelId, guildId, instanceId) {
    if (!channelId) {
        console.warn('%s from_platform GET skipped — no channelId', LOG_TAG);
        return null;
    }
    console.info('%s from_platform GET channel=%s guild=%s instance=%s',
        LOG_TAG, channelId, guildId, instanceId);
    const query = new URLSearchParams({
        platform_id: String(PLATFORM_DISCORD_ID),
        binding_key: channelId,
    });
    try {
        const response = await fetch(
            LOBBY_FROM_PLATFORM_URL + '?' + query.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Accept': 'application/json' },
            });
        if (!response.ok) {
            console.error('%s from_platform GET not ok status=%s',
                LOG_TAG, response.status);
            return null;
        }
        const payload = await response.json();
        const lobbies = (payload && Array.isArray(payload.lobbies))
            ? payload.lobbies : [];
        console.info('%s from_platform GET ok lobby_count=%s',
            LOG_TAG, lobbies.length);
        return payload;
    } catch (error) {
        console.error('%s from_platform GET transport error: %s',
            LOG_TAG, error && error.message);
        return null;
    }
}


/**
 * Derive a channel-lobby entry's display state from its lobby payload.
 *
 * LobbySerializer ships `status` as a nested `{ id, name }` (status_id
 * is write-only) and the bound game as the `game` FK pk, so both are
 * read defensively. Returns:
 *   'forming'      — FORMING lobby, still gathering players.
 *   'in_progress'  — LAUNCHING / PLAYING with a bound game.
 *   null           — skip this entry. PLAYING-with-no-game is logged
 *                    (shouldn't happen, must not crash); anything else
 *                    unrecognized is skipped silently.
 */
function deriveDisplayState(lobby) {
    const statusId = (lobby && typeof lobby.status_id === 'number')
        ? lobby.status_id
        : ((lobby && lobby.status && lobby.status.id) || null);
    const gameId = (lobby && lobby.current_game_id != null)
        ? lobby.current_game_id
        : (lobby ? lobby.game : null);
    if (statusId === LOBBY_STATUS_FORMING_ID) {
        return CHANNEL_LOBBY_STATE_FORMING;
    }
    if ((statusId === LOBBY_STATUS_LAUNCHING_ID
            || statusId === LOBBY_STATUS_PLAYING_ID) && gameId != null) {
        return CHANNEL_LOBBY_STATE_IN_PROGRESS;
    }
    if (statusId === LOBBY_STATUS_PLAYING_ID && gameId == null) {
        console.warn('%s channel lobby PLAYING with no game; skipping'
            + ' uuid=%s', LOG_TAG, lobby && lobby.uuid);
    }
    return null;
}


/**
 * Map the raw from_platform GET `lobbies` array onto the shape MainMenu
 * consumes: each entry keeps its backend fields and gains `displayState`
 * (see deriveDisplayState) and `userOwnsSeat` (true when the backend
 * reported a non-null current_user_seat_index). Entries that derive to a
 * null displayState are dropped.
 */
function deriveChannelLobbies(lobbies) {
    const out = [];
    for (const entry of (lobbies || [])) {
        const displayState = deriveDisplayState(entry && entry.lobby);
        if (displayState === null) {
            continue;
        }
        out.push({
            ...entry,
            displayState,
            userOwnsSeat: !!entry && entry.current_user_seat_index != null,
        });
    }
    return out;
}


async function fetchFromPlatformPayload(body) {
    const payload = await postFromPlatform(body);
    // CC_POLITE_JOIN_BOUNCES (2026-06-07): postFromPlatform now returns
    // an `{ error, status }` shape on failure instead of null, so a
    // failed join is truthy. Only a real seated/rejoin payload may fire
    // the joined event — an error shape must never masquerade as a
    // successful join (it would seed Lobby.js / GamePage with garbage).
    if (payload && !payload.error && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(
            DISCORD_LOBBY_JOINED_EVENT, { detail: payload }));
    }
    return payload;
}


/**
 * Build a Discord `from_platform` body — `binding_key = channelId` with
 * guild / instance metadata. Centralizes the per-platform body shape so
 * Discord callers don't repeat the literal.
 */
function _buildDiscordBody(channelId, guildId, instanceId, extra) {
    return {
        platform_id: PLATFORM_DISCORD_ID,
        binding_key: channelId,
        binding_metadata: {
            guild_id: guildId || '',
            instance_id: instanceId || '',
        },
        ...extra,
    };
}


/**
 * Menu-driven join: POST /api/lobby/from_platform/ with an explicit
 * `lobby_uuid` to seat the user into the chosen channel lobby, then fire
 * the `discord-lobby-joined` window event so a freshly-mounted Lobby.js
 * (after the caller routes to /casual) picks up the seated payload.
 *
 * CC_DISCORD_MAIN_MENU (2026-05-30): the boot fetch became a read-only
 * GET, so this is now the only path that POSTs from_platform — exported
 * for MainMenu's "Live in this channel" Join / Rejoin rows. Callers
 * should route to /casual BEFORE awaiting this so the Lobby.js event
 * listener is mounted before the POST resolves and fires the event.
 */
export async function joinChannelLobby(channelId, guildId, instanceId,
        lobbyUuid) {
    if (!channelId) {
        console.warn('%s joinChannelLobby skipped — no channelId', LOG_TAG);
        return null;
    }
    return fetchFromPlatformPayload(_buildDiscordBody(
        channelId, guildId, instanceId, { lobby_uuid: lobbyUuid }));
}

/**
 * Menu-driven New Game (Discord): POST /api/lobby/from_platform/ with
 * `create_new: true` so the backend always mints a fresh channel-bound
 * lobby (never finds-or-joins an existing one), then fire
 * `discord-lobby-joined` so a freshly-mounted Lobby.js picks up the
 * seated payload.
 *
 * CC_DISCORD_LOBBY_BULLETPROOF (2026-05-31, invariant I2/I3): this is
 * the Discord create path that replaces the dispatcher auto-create.
 * Discord New Game must go through here (a DISCORD-bound lobby), not the
 * open-web WS Join(0) path. Callers route to /casual BEFORE awaiting so
 * Lobby.js's listener is mounted before the POST resolves.
 */
export async function createChannelLobby(channelId, guildId, instanceId) {
    if (!channelId) {
        console.warn('%s createChannelLobby skipped — no channelId', LOG_TAG);
        return null;
    }
    return fetchFromPlatformPayload(_buildDiscordBody(
        channelId, guildId, instanceId, { create_new: true }));
}


/**
 * Open-web rendezvous: POST /api/lobby/from_platform/ with the BROWSER
 * platform id + `binding_key = str(user.id)` to bind the freshly-opened
 * WebSocket UserConnection to the lobby identified by `lobbyUuid`.
 *
 * This is the open-web counterpart to `joinChannelLobby`. It's the fix
 * for the "Loading group… forever after browser refresh" hang
 * (LOBBY_GAME_BOUNDARY_AUDIT §3.3 failure mode (a) for the open-web
 * rail): on a hard refresh / bookmark / direct-URL entry to
 * `/game/:lobbyUuid`, the user never goes through the picker rail
 * (LobbiesPage's `joinGroup(0)` → WS `join_lobby_for_user_connection`),
 * so the new WS UserConnection has `lobby=null` and the Members
 * broadcast never lands. POSTing here routes to
 * `_from_platform_join_by_uuid` → `_rendezvous_ws` →
 * `join_lobby_for_user_connection`, which is exactly what binds the
 * connection and fires Members.
 *
 * Scope today: the lobby owner — `create_browser_lobby` writes the
 * `binding_key = str(user.id)` binding at lobby creation, so a refresh
 * by the owner finds the binding and `join_lobby_by_uuid`'s bind check
 * passes. For a non-owner (Player B joining Player A's lobby via the
 * WS picker rail, then refreshing) there is no BROWSER+str(B.id)
 * binding, and the call will 4xx with `lobby_not_bound_to_channel` —
 * Lobby.js continues to spin. Multi-player open-web refresh is the
 * follow-up; a dedicated `/api/lobby/<uuid>/rendezvous/` endpoint
 * (bind check = "user has a Seat in this lobby") is the cleanest
 * shape, but it's out of scope for this fix.
 *
 * Fires the legacy `discord-lobby-joined` window event symmetrically
 * with `joinChannelLobby` so `Lobby.js` / `GamePage.js` listeners pick
 * up the seated payload without per-platform branching.
 */
export async function joinBrowserLobby(userId, lobbyUuid) {
    if (!userId) {
        console.warn('%s joinBrowserLobby skipped — no userId', LOG_TAG);
        return null;
    }
    return fetchFromPlatformPayload({
        platform_id: PLATFORM_BROWSER_ID,
        binding_key: String(userId),
        lobby_uuid: lobbyUuid,
    });
}

function markDiscordAuthFailed() {
    if (typeof window === 'undefined') {
        return;
    }
    window[DISCORD_AUTH_STATE_FLAG] = 'failed';
    window.dispatchEvent(new CustomEvent(DISCORD_AUTH_FAILED_EVENT));
}

if (typeof window !== 'undefined'
        && window[DISCORD_AUTH_STATE_FLAG] === undefined) {
    window[DISCORD_AUTH_STATE_FLAG] = 'pending';
}


/**
 * Read a cookie value by name. Django sets `csrftoken` without
 * HttpOnly so the SPA can read it and echo it back as a header
 * — matches the pattern in components/api/API.js.
 */
function readCookie(name) {
    if (typeof document === 'undefined' || !document.cookie) {
        return null;
    }
    const prefix = name + '=';
    const parts = document.cookie.split(';');
    for (let part of parts) {
        part = part.trim();
        if (part.indexOf(prefix) === 0) {
            return decodeURIComponent(part.substring(prefix.length));
        }
    }
    return null;
}


export class DiscordProvider extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isEmbedded: isLikelyEmbedded(),
            isReady: false,
            sdk: null,
            channelId: null,
            guildId: null,
            instanceId: null,
            discordUserId: null,
            discordUsername: null,
            discordAvatarHash: null,
            discordGlobalName: null,
            discordNick: null,
            isAuthenticatedHsh: false,
            isAuthenticatedSdk: false,
            error: null,
            lobby: null,
            lobbyIsOwner: null,
            lobbySeatIndex: null,
            rejoinGameId: null,
            channelLobbies: [],
            channelLobbiesFetched: false,
            bootStages: makeInitialBootStages(),
        };
        // Pending rejoin game id stashed when app.game isn't ready yet,
        // plus its bounded-retry timer handle. Instance fields, not
        // state — they drive a side dispatch, not a render.
        this._pendingRejoinGameId = null;
        this._rejoinFlushTimer = null;
        // from_platform channel subscription (CC_DISCORD_LOBBY_LIFECYCLE
        // §5): pending channel id + bounded-retry handle, same shape as
        // the rejoin flush — the subscribe needs app.game.gameApi, which
        // can mount after the handshake settles.
        this._pendingSubscribeChannelId = null;
        this._subscribeFlushTimer = null;
    }

    componentWillUnmount() {
        // Clear deferred-dispatch timers and drop the push listener. No
        // navigation, group leave, or other persistent UI side-effect
        // here — those are the StrictMode unmount trap (see CLAUDE.md).
        if (this._rejoinFlushTimer) {
            clearTimeout(this._rejoinFlushTimer);
            this._rejoinFlushTimer = null;
        }
        if (this._subscribeFlushTimer) {
            clearTimeout(this._subscribeFlushTimer);
            this._subscribeFlushTimer = null;
        }
        window.removeEventListener(
            'from-platform-changed', this._onFromPlatformChanged);
    }

    /**
     * Re-fetch the channel lobby list when a peer mutates channel state
     * (CC_DISCORD_LOBBY_LIFECYCLE §5). Arrow field so the same reference
     * is added and removed as the window listener. Gated on embedded +
     * channelId so a stray event in open-web mode is a no-op.
     */
    _onFromPlatformChanged = () => {
        if (!this.state.isEmbedded || !this.state.channelId) {
            return;
        }
        console.info('%s from_platform.changed → re-fetching channel'
            + ' lobbies', LOG_TAG);
        this.fetchChannelLobbies(
            this.state.channelId, this.state.guildId, this.state.instanceId);
    };

    /**
     * Join this client to the channel's from_platform push group. Fires
     * immediately when app.game.gameApi exists; otherwise stashes the
     * channel id and retries on a bounded one-shot series (the API
     * singleton can mount after the Discord handshake settles).
     */
    _subscribeToChannel(channelId) {
        if (!channelId) {
            return;
        }
        const app = this.props.app;
        if (app && app.game && app.game.gameApi) {
            app.game.gameApi.subscribeFromPlatformChannel(channelId);
            return;
        }
        this._pendingSubscribeChannelId = channelId;
        this._scheduleSubscribeFlush(0);
    }

    /**
     * Bounded one-shot retry for the deferred channel subscription.
     * Mirrors _scheduleRejoinFlush so it can never spin forever.
     */
    _scheduleSubscribeFlush(attempt) {
        if (attempt >= REJOIN_FLUSH_MAX_ATTEMPTS) {
            console.warn('%s channel subscribe deferred but app.game never'
                + ' became ready after %d attempts; giving up',
                LOG_TAG, attempt);
            this._pendingSubscribeChannelId = null;
            return;
        }
        this._subscribeFlushTimer = setTimeout(() => {
            this._subscribeFlushTimer = null;
            if (!this._pendingSubscribeChannelId) {
                return;
            }
            const app = this.props.app;
            if (app && app.game && app.game.gameApi) {
                const channelId = this._pendingSubscribeChannelId;
                this._pendingSubscribeChannelId = null;
                app.game.gameApi.subscribeFromPlatformChannel(channelId);
                return;
            }
            this._scheduleSubscribeFlush(attempt + 1);
        }, REJOIN_FLUSH_INTERVAL_MS);
    }

    /**
     * Patch one cold-start stage. Functional setState so concurrent
     * stage transitions (e.g. a late from_platform resolving after the
     * SDK leg) don't clobber each other.
     */
    _setStage(name, patch) {
        this.setState((prev) => ({
            bootStages: {
                ...prev.bootStages,
                [name]: { ...prev.bootStages[name], ...patch },
            },
        }));
    }

    /**
     * Boot fetch (CC_DISCORD_MAIN_MENU, 2026-05-30): read the
     * channel-bound lobbies list with a GET and stash it on context for
     * MainMenu's "Live in this channel" section. Replaces the old
     * POST-on-boot that auto-seated every Discord user — boot now seats
     * no one, dispatches no `discord-lobby-joined`, and leaves
     * context.lobby / lobbyIsOwner / lobbySeatIndex null until a later
     * user action (a menu Join) POSTs via joinChannelLobby. The channel
     * binding is still stashed on app for resumeGame.
     */
    async fetchChannelLobbies(channelId, guildId, instanceId) {
        if (this.props.app) {
            this.props.app.discordChannelBinding = {
                channel_id: channelId || '',
                guild_id: guildId || '',
                instance_id: instanceId || '',
            };
        }
        this._setStage('lobbyFetch', {
            status: BOOT_STAGE_PENDING,
            startedAt: Date.now(),
        });
        const payload = await getFromPlatform(channelId, guildId, instanceId);
        const lobbies = (payload && Array.isArray(payload.lobbies))
            ? payload.lobbies : [];
        const channelLobbies = deriveChannelLobbies(lobbies);
        this._setStage('lobbyFetch', payload
            ? { status: BOOT_STAGE_OK, completedAt: Date.now() }
            : {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_FROM_PLATFORM_NULL,
            });
        console.info('%s channelLobbies populated count=%s fetched=true',
            LOG_TAG, channelLobbies.length);
        this.setState({ channelLobbies, channelLobbiesFetched: true });
        // Join the channel's from_platform push group so peer mutations
        // repaint this menu live (CC_DISCORD_LOBBY_LIFECYCLE §5).
        this._subscribeToChannel(channelId);
        return payload;
    }

    /**
     * POST to /api/lobby/from_platform/, fire the
     * `discord-lobby-joined` window event for already-mounted listeners,
     * and capture the lobby payload onto context state so late-mounting
     * consumers (e.g. Lobby.js after navigation) can read it without
     * racing the one-shot event.
     *
     * CC_DISCORD_MAIN_MENU retired this from the boot path (boot now
     * GETs via fetchChannelLobbies). Retained — DiscordProvider.rejoin
     * tests drive it directly to cover the rejoin pre-branch, and the
     * bare find-or-create response shape it handles is still the one the
     * backend returns for that path.
     */
    async dispatchLobbyJoined(channelId, guildId, instanceId) {
        // Stash the channel binding on the App so gameApi.resumeGame can
        // attach it to the WS Resume package (the engine needs it to
        // find and re-home the boot lobby). Set before any rejoin
        // dispatch below so resumeGame can read it. Null-safe: the
        // open-web provider has no `app` prop.
        if (this.props.app) {
            this.props.app.discordChannelBinding = {
                channel_id: channelId || '',
                guild_id: guildId || '',
                instance_id: instanceId || '',
            };
        }
        this._setStage('lobbyFetch', {
            status: BOOT_STAGE_PENDING,
            startedAt: Date.now(),
        });
        const payload = await fetchFromPlatformPayload(
            channelId, guildId, instanceId);
        this._setStage('lobbyFetch', payload
            ? { status: BOOT_STAGE_OK, completedAt: Date.now() }
            : {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_FROM_PLATFORM_NULL,
            });

        // Rejoin route: the backend sent us straight back into an
        // in-progress game. Do NOT populate context.lobby (the Lobby
        // Navigator must not navigate) — capture the game id for
        // diagnostics and dispatch the WS Resume instead.
        if (payload && payload.action === ACTION_REJOIN) {
            console.info('%s rejoin route game_id=%s', LOG_TAG,
                payload.game_id);
            this.setState({ rejoinGameId: payload.game_id });
            this._dispatchRejoinResume(payload.game_id);
            return payload;
        }
        // `from_platform` returns `{ lobby_id, is_owner, seat_index,
        // lobby: { uuid, seats, ... } }`. Lobby.js reads
        // `lobby.seats[]`, so surface the nested object when present
        // and fall back to the bare payload if the backend ever ships
        // a flatter shape.
        const lobby = (payload && payload.lobby) || payload || null;
        const seats = (lobby && lobby.seats) || [];
        // `is_owner` / `seat_index` live on the top-level payload, NOT
        // the nested lobby object. Treat missing/typed-wrong values as
        // `null` — the unambiguous "not populated yet" sentinel that
        // matches the context default. Do not collapse to false/0.
        const lobbyIsOwner = payload && typeof payload.is_owner === 'boolean'
            ? payload.is_owner
            : null;
        const lobbySeatIndex = payload && typeof payload.seat_index === 'number'
            ? payload.seat_index
            : null;
        console.info('%s context.lobby set uuid=%s seats=%d isOwner=%s seat=%s',
            LOG_TAG, lobby && lobby.uuid, seats.length,
            lobbyIsOwner, lobbySeatIndex);
        this.setState({ lobby, lobbyIsOwner, lobbySeatIndex });
        return payload;
    }

    /**
     * Dispatch the WS Resume for a rejoin. If App has already built its
     * `game` singleton, fire immediately; otherwise stash the id and
     * flush it on a bounded retry once `app.game.gameApi` exists.
     */
    _dispatchRejoinResume(gameId) {
        const app = this.props.app;
        if (app && app.game && app.game.gameApi) {
            app.game.gameApi.resumeGame(gameId);
            return;
        }
        this._pendingRejoinGameId = gameId;
        this._scheduleRejoinFlush(0);
    }

    /**
     * One-shot timeout that re-checks for app.game.gameApi and fires the
     * stashed resume. Re-arms itself up to REJOIN_FLUSH_MAX_ATTEMPTS,
     * then gives up loudly. Bounded so it cannot spin forever.
     */
    _scheduleRejoinFlush(attempt) {
        if (attempt >= REJOIN_FLUSH_MAX_ATTEMPTS) {
            console.warn('%s rejoin resume deferred but app.game never'
                + ' became ready after %d attempts; giving up',
                LOG_TAG, attempt);
            this._pendingRejoinGameId = null;
            return;
        }
        this._rejoinFlushTimer = setTimeout(() => {
            this._rejoinFlushTimer = null;
            if (this._pendingRejoinGameId === null
                    || this._pendingRejoinGameId === undefined) {
                return;
            }
            const app = this.props.app;
            if (app && app.game && app.game.gameApi) {
                const gameId = this._pendingRejoinGameId;
                this._pendingRejoinGameId = null;
                app.game.gameApi.resumeGame(gameId);
                return;
            }
            this._scheduleRejoinFlush(attempt + 1);
        }, REJOIN_FLUSH_INTERVAL_MS);
    }

    componentDidMount() {
        if (!this.state.isEmbedded) {
            // Open-web path. Nothing to initialize. The default context
            // value (isEmbedded: false, everything else null) is already
            // correct.
            console.info('%s open-web mode; SDK skipped', LOG_TAG);
            return;
        }
        // We are embedded — record that the detect leg resolved ok
        // immediately (it's a synchronous URL check, so startedAt and
        // completedAt are the same instant).
        const detectedAt = Date.now();
        this._setStage('embeddedDetect', {
            status: BOOT_STAGE_OK,
            startedAt: detectedAt,
            completedAt: detectedAt,
        });
        // Listen for peer-driven channel mutations (§5). Embedded-only:
        // open-web returned above, so this listener is never armed there.
        window.addEventListener(
            'from-platform-changed', this._onFromPlatformChanged);
        if (!DISCORD_APP_ID) {
            console.warn('%s embedded but REACT_APP_DISCORD_APP_ID is unset;'
                + ' SDK cannot initialize', LOG_TAG);
            this.setState({ error: ERROR_MISSING_APP_ID });
            this._setStage('sdkReady', {
                status: BOOT_STAGE_FAILED,
                startedAt: detectedAt,
                completedAt: detectedAt,
                error: ERROR_MISSING_APP_ID,
            });
            markDiscordAuthFailed();
            return;
        }
        this.initializeSdk();
    }

    async initializeSdk() {
        console.info('%s embedded mode detected; initializing SDK', LOG_TAG);
        this._setStage('sdkReady', {
            status: BOOT_STAGE_PENDING,
            startedAt: Date.now(),
        });
        let sdk;
        try {
            sdk = new DiscordSDK(DISCORD_APP_ID);
            await sdk.ready();
        } catch (error) {
            const message = (error && error.message) || ERROR_INIT_FAILED;
            console.error('%s SDK initialization failed: %s',
                LOG_TAG, error && error.message);
            this._setStage('sdkReady', {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: message,
            });
            this.setState({ error: message });
            markDiscordAuthFailed();
            return;
        }
        this._setStage('sdkReady', {
            status: BOOT_STAGE_OK,
            completedAt: Date.now(),
        });
        console.info('%s SDK ready; channelId=%s guildId=%s instanceId=%s',
            LOG_TAG, sdk.channelId, sdk.guildId, sdk.instanceId);
        this.setState({
            isReady: true,
            sdk,
            channelId: sdk.channelId || null,
            guildId: sdk.guildId || null,
            instanceId: sdk.instanceId || null,
        });

        // Outer catch so an unexpected throw inside runAuthHandshake
        // still leaves the provider in a stable, reported state rather
        // than unwinding silently.
        try {
            await this.runAuthHandshake(sdk);
        } catch (error) {
            console.error('%s auth handshake unexpected error: %s',
                LOG_TAG, error && error.message);
            this.setState({
                error: (error && error.message) || 'handshake-failed',
            });
            markDiscordAuthFailed();
        }
    }

    /**
     * Complete the three-leg Discord Activity sign-in:
     *
     *   1. sdk.commands.authorize → Discord OAuth code
     *   2. POST /api/session_auth/discord/ → HSH session cookie
     *      + one-shot discord_access_token
     *   3. sdk.commands.authenticate → completes the SDK's internal
     *      handshake (enables privileged commands)
     */
    async runAuthHandshake(sdk) {
        console.info('%s starting auth handshake', LOG_TAG);

        // Step 1: ask Discord for an OAuth authorization code.
        this._setStage('authorize', {
            status: BOOT_STAGE_PENDING,
            startedAt: Date.now(),
        });
        let authorizeResult;
        try {
            authorizeResult = await sdk.commands.authorize({
                client_id: DISCORD_APP_ID,
                response_type: 'code',
                state: '',
                prompt: 'none',
                scope: SDK_SCOPES,
            });
        } catch (error) {
            console.error('%s authorize() failed: %s',
                LOG_TAG, error && error.message);
            this._setStage('authorize', {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_AUTHORIZE_FAILED,
            });
            this.setState({ error: ERROR_AUTHORIZE_FAILED });
            markDiscordAuthFailed();
            return;
        }
        const code = authorizeResult && authorizeResult.code;
        if (!code) {
            console.error('%s authorize() returned no code', LOG_TAG);
            this._setStage('authorize', {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_AUTHORIZE_NO_CODE,
            });
            this.setState({ error: ERROR_AUTHORIZE_NO_CODE });
            markDiscordAuthFailed();
            return;
        }
        this._setStage('authorize', {
            status: BOOT_STAGE_OK,
            completedAt: Date.now(),
        });
        console.info('%s authorize() ok; posting code to backend', LOG_TAG);

        // Step 2: POST the code to the backend. Prime the csrftoken
        // cookie first — idempotent if API.js already did it, and the
        // DiscordProvider's componentDidMount can race API's bootstrap.
        let backendResponse;
        try {
            this._setStage('csrfPrime', {
                status: BOOT_STAGE_PENDING,
                startedAt: Date.now(),
            });
            await fetch(CSRF_URL, {
                method: 'GET',
                credentials: 'include',
            });
            this._setStage('csrfPrime', {
                status: BOOT_STAGE_OK,
                completedAt: Date.now(),
            });
            const csrfToken = readCookie(CSRF_COOKIE_NAME);
            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
            if (csrfToken) {
                headers[CSRF_HEADER_NAME] = csrfToken;
            }
            this._setStage('hshSession', {
                status: BOOT_STAGE_PENDING,
                startedAt: Date.now(),
            });
            const response = await fetch(DISCORD_AUTH_URL, {
                method: 'POST',
                credentials: 'include',
                headers,
                body: JSON.stringify({ code, guild_id: sdk.guildId || '' }),
            });
            if (!response.ok) {
                let body = '';
                try {
                    body = await response.text();
                } catch (readError) {
                    body = '';
                }
                console.error('%s backend POST failed status=%s body=%s',
                    LOG_TAG, response.status, body && body.slice(0, 200));
                this._setStage('hshSession', {
                    status: BOOT_STAGE_FAILED,
                    completedAt: Date.now(),
                    error: 'backend-' + response.status,
                });
                this.setState({ error: 'backend-' + response.status });
                markDiscordAuthFailed();
                return;
            }
            backendResponse = await response.json();
            this._setStage('hshSession', {
                status: BOOT_STAGE_OK,
                completedAt: Date.now(),
            });
        } catch (error) {
            console.error('%s backend POST transport error: %s',
                LOG_TAG, error && error.message);
            // The shared try wraps both the csrf and the discord fetch;
            // a transport throw lands on whichever was in flight. Mark
            // the session leg failed (the csrf leg is already ok by the
            // time we POST, and a csrf-prime throw leaves hshSession at
            // null-startedAt so the panel renders no spurious duration).
            this._setStage('hshSession', {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_BACKEND_TRANSPORT,
            });
            this.setState({ error: ERROR_BACKEND_TRANSPORT });
            markDiscordAuthFailed();
            return;
        }

        const accessToken = backendResponse
            && backendResponse.discord_access_token;
        if (!accessToken) {
            console.error('%s backend response missing'
                + ' discord_access_token', LOG_TAG);
            // The SDK leg cannot run without a token — surface it as a
            // failed sdkAuthenticate so the panel shows the blocked leg.
            this._setStage('sdkAuthenticate', {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_NO_ACCESS_TOKEN,
            });
            this.setState({
                error: ERROR_NO_ACCESS_TOKEN,
                isAuthenticatedHsh: true,
                // discord_username is the real Discord @handle from
                // SocialAccount.provider_username — NOT backendResponse
                // .username (the synthetic discord_<snowflake> Django
                // username), which must never be a render candidate.
                discordUsername: backendResponse.discord_username || null,
                discordGlobalName:
                    backendResponse.discord_global_name || null,
                discordNick: backendResponse.discord_nick || null,
            });
            // HSH session cookie IS set; only the SDK leg is blocked.
            // The App's auth-bootstrap only needs whoami to succeed.
            markDiscordAuthComplete(backendResponse);
            await this.fetchChannelLobbies(
                sdk.channelId, sdk.guildId, sdk.instanceId);
            return;
        }
        console.info('%s backend ok; running sdk.authenticate', LOG_TAG);

        // Step 3: complete the SDK's internal auth handshake.
        this._setStage('sdkAuthenticate', {
            status: BOOT_STAGE_PENDING,
            startedAt: Date.now(),
        });
        try {
            await sdk.commands.authenticate({ access_token: accessToken });
        } catch (error) {
            console.error('%s sdk.authenticate() failed: %s',
                LOG_TAG, error && error.message);
            this._setStage('sdkAuthenticate', {
                status: BOOT_STAGE_FAILED,
                completedAt: Date.now(),
                error: ERROR_SDK_AUTHENTICATE_FAILED,
            });
            // The HSH-side session is valid (we have the user-identity
            // payload), but the SDK is not authenticated. Surface that
            // split state so callers can decide whether to retry.
            this.setState({
                error: ERROR_SDK_AUTHENTICATE_FAILED,
                discordUserId: backendResponse.id || null,
                // Real Discord @handle, not the snowflake Django username.
                discordUsername: backendResponse.discord_username || null,
                discordAvatarHash:
                    backendResponse.discord_avatar_hash || null,
                discordGlobalName:
                    backendResponse.discord_global_name || null,
                discordNick: backendResponse.discord_nick || null,
                isAuthenticatedHsh: true,
                isAuthenticatedSdk: false,
            });
            // Same reasoning as the no-access-token branch: HSH session
            // is valid; only the SDK leg is blocked.
            markDiscordAuthComplete(backendResponse);
            await this.fetchChannelLobbies(
                sdk.channelId, sdk.guildId, sdk.instanceId);
            return;
        }
        this._setStage('sdkAuthenticate', {
            status: BOOT_STAGE_OK,
            completedAt: Date.now(),
        });
        console.info('%s auth handshake complete', LOG_TAG);

        this.setState({
            discordUserId: backendResponse.id || null,
            // Real Discord @handle, not the snowflake Django username.
            discordUsername: backendResponse.discord_username || null,
            discordAvatarHash: backendResponse.discord_avatar_hash || null,
            discordGlobalName: backendResponse.discord_global_name || null,
            discordNick: backendResponse.discord_nick || null,
            isAuthenticatedHsh: true,
            isAuthenticatedSdk: true,
        });
        markDiscordAuthComplete(backendResponse);
        await this.fetchChannelLobbies(
            sdk.channelId, sdk.guildId, sdk.instanceId);
    }

    render() {
        return (
            <DiscordContext.Provider value={this.state}>
                {this.props.children}
            </DiscordContext.Provider>
        );
    }
}

